/**
 * Fixed-width text formatter for thermal receipts.
 * Pure functions ÔÇö no business logic.
 */

import { withWesternDigits, getDisplayLocale } from '../../utils/formatSafety';
import { toDisplayAmount } from '../../utils/posCurrencyUtils';

export function normalizeText(value) {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim();
}

export function repeat(char, count) {
  return char.repeat(Math.max(0, count));
}

export function padRight(text, width) {
  const value = normalizeText(text);
  if (value.length >= width) return value.slice(0, width);
  return value + repeat(' ', width - value.length);
}

export function padLeft(text, width) {
  const value = normalizeText(text);
  if (value.length >= width) return value.slice(-width);
  return repeat(' ', width - value.length) + value;
}

export function center(text, width) {
  const value = normalizeText(text);
  if (value.length >= width) return value.slice(0, width);
  const left = Math.floor((width - value.length) / 2);
  return repeat(' ', left) + value + repeat(' ', width - value.length - left);
}

export function wrapText(text, width) {
  const value = normalizeText(text);
  if (!value) return [''];
  if (value.length <= width) return [value];

  const words = value.split(' ');
  const lines = [];
  let current = '';

  for (const word of words) {
    if (word.length > width) {
      if (current) {
        lines.push(current);
        current = '';
      }
      for (let i = 0; i < word.length; i += width) {
        lines.push(word.slice(i, i + width));
      }
      continue;
    }

    const next = current ? `${current} ${word}` : word;
    if (next.length <= width) {
      current = next;
    } else {
      if (current) lines.push(current);
      current = word;
    }
  }

  if (current) lines.push(current);
  return lines.length ? lines : [''];
}

export function joinColumns(parts, widths, alignments) {
  return parts
    .map((part, index) => {
      const width = widths[index] ?? 0;
      const align = alignments[index] || 'left';
      if (align === 'right') return padLeft(part, width);
      if (align === 'center') return center(part, width);
      return padRight(part, width);
    })
    .join('');
}

export function formatThermalAmount(amount, options = {}) {
  const raw = Number(amount);
  const safe = Number.isFinite(raw) ? raw : 0;
  let code = String(options.currencyCode || 'MRO').toUpperCase();
  if (code === 'UM') code = 'MRU';

  // Storage is always MRO — convert via posCurrencyUtils SSOT (not a parallel /10).
  let numeric =
    options.convertMroToMru === false
      ? safe
      : toDisplayAmount(safe, code === 'MRU' ? 'MRU' : 'MRO');

  const compact = options.compact !== false;

  if (compact && Math.abs(numeric - Math.round(numeric)) < 0.001) {
    return String(Math.round(numeric));
  }

  return numeric.toFixed(2).replace('.', ',');
}

export function formatThermalCurrencyParts(amount, config = {}) {
  const language = String(config.language || 'fr').toLowerCase();
  const rtl = language.startsWith('ar');
  const numeric = Number(amount);
  const safe = Number.isFinite(numeric) ? numeric : 0;
  const prefix = safe < 0 ? '-' : '';
  const formatted = formatThermalAmount(Math.abs(safe), config);
  const label = config.currencyLabel || config.currencyCode || 'MRO';
  return {
    amount: `${prefix}${formatted}`,
    currency: label,
    rtl
  };
}

export function formatThermalCurrency(amount, config = {}) {
  const { amount: formatted, currency: label, rtl } = formatThermalCurrencyParts(amount, config);
  // Match UI: amount then currency. Arabic uses RLI so printers/browsers keep that reading order.
  if (rtl) {
    return `\u2067${formatted}\u00A0${label}\u2069`;
  }
  return `${formatted} ${label}`;
}

export function formatQtyPrice(qty, unitPrice, config = {}) {
  const q = Number(qty) || 0;
  const price = formatThermalAmount(unitPrice, config);
  return `${q} x${price}`;
}

export function formatReceiptDate(date, locale = getDisplayLocale(false)) {
  const value = date instanceof Date ? date : new Date(date || Date.now());
  if (String(locale).toLowerCase().startsWith('ar')) {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}/${month}/${day}`;
  }
  return value.toLocaleDateString(locale, withWesternDigits(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }));
}

export function formatReceiptTime(date, locale = getDisplayLocale(false)) {
  const value = date instanceof Date ? date : new Date(date || Date.now());
  if (String(locale).toLowerCase().startsWith('ar')) {
    const h = String(value.getHours()).padStart(2, '0');
    const m = String(value.getMinutes()).padStart(2, '0');
    const s = String(value.getSeconds()).padStart(2, '0');
    return `${h}:${m}:${s}`;
  }
  return value.toLocaleTimeString(locale, withWesternDigits(locale, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  }));
}

export function separatorLine(width, char = '-') {
  return repeat(char, width);
}

export function pairLine(label, value, width, rtl = false) {
  const labelText = normalizeText(label);
  const valueText = normalizeText(value);
  const valueWidth = Math.min(width - 1, Math.max(8, valueText.length));
  const labelWidth = Math.max(1, width - valueWidth);
  if (rtl) {
    return padRight(valueText, valueWidth) + padLeft(labelText, labelWidth);
  }
  return padRight(labelText, labelWidth) + padLeft(valueText, valueWidth);
}
